import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Slider,
  Stack,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  SkipPrevious,
  SkipNext,
  Replay10,
  Forward10,
  Summarize,
  Assessment,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { serverConfig } from "../config";
// Remove socket.io-client import since we're using native WebSocket

const TranscriptContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(1, 0),
  maxHeight: "400px",
  overflowY: "auto",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: theme.palette.grey[100],
  },
  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.grey[400],
    borderRadius: "4px",
  },
}));

const TranscriptSegment = styled(Box)(
  ({ theme, isHighlighted, isClickable }) => ({
    padding: theme.spacing(1),
    margin: theme.spacing(0.5, 0),
    borderRadius: theme.shape.borderRadius,
    cursor: isClickable ? "pointer" : "default",
    backgroundColor: isHighlighted
      ? theme.palette.primary.light
      : "transparent",
    border: isHighlighted
      ? `2px solid ${theme.palette.primary.main}`
      : "1px solid transparent",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: isClickable ? theme.palette.action.hover : "transparent",
    },
  })
);

const AudioPlayerContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(2, 0),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
}));

const RealTimeTranscript = ({
  meetingId,
  audioUrl,
  websocketUrl = process.env.REACT_APP_WEBSOCKET_URL ||
    "wss://livebots.instaconnect.io/ws",
  onTranscriptUpdate,
}) => {
  // State management
  const [socket, setSocket] = useState(null);
  const [transcriptSegments, setTranscriptSegments] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [highlightedSegmentId, setHighlightedSegmentId] = useState(null);

  // Meeting status and tabs
  const [meetingEnded, setMeetingEnded] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [summary, setSummary] = useState(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [summaryGenerated, setSummaryGenerated] = useState(false); // Track if summary was ever generated

  // Audio player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Refs
  const audioRef = useRef(null);
  const transcriptContainerRef = useRef(null);
  const socketRef = useRef(null);

  // Load existing transcript from database
  const loadExistingTranscript = async () => {
    try {
      setIsLoading(true);
      const backendUrl = serverConfig.serverURI || "https://laptop:3000";
      console.log(
        "Loading transcript from:",
        `${backendUrl}/api/meetings/transcript/${meetingId}`
      );
      const response = await fetch(
        `${backendUrl}/api/meetings/transcript/${meetingId}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.transcript && data.transcript.segments) {
          console.log(
            "Loaded existing transcript:",
            data.transcript.segments.length,
            "segments"
          );
          setTranscriptSegments(data.transcript.segments);
        }
      } else if (response.status !== 404) {
        console.warn("Failed to load existing transcript:", response.status);
      }
    } catch (error) {
      console.error("Error loading existing transcript:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load existing transcript on component mount
  useEffect(() => {
    if (!meetingId) return;
    loadExistingTranscript();
  }, [meetingId]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!meetingId) return;

    let reconnectInterval;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;

    const connectWebSocket = () => {
      try {
        const newSocket = new WebSocket(websocketUrl);

        socketRef.current = newSocket;
        setSocket(newSocket);

        // Connection event handlers
        newSocket.onopen = () => {
          console.log("Connected to WebSocket server");
          setIsConnected(true);
          setConnectionError(null);
          reconnectAttempts = 0;

          // Send initial messages
          newSocket.send(
            JSON.stringify({
              type: "start_meeting",
              meeting_id: meetingId,
            })
          );

          // Request transcript history
          newSocket.send(
            JSON.stringify({
              type: "get_transcript",
              meeting_id: meetingId,
            })
          );
        };

        newSocket.onclose = () => {
          console.log("Disconnected from WebSocket server");
          setIsConnected(false);

          // Attempt to reconnect
          if (reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            console.log(
              `Attempting to reconnect... (${reconnectAttempts}/${maxReconnectAttempts})`
            );
            reconnectInterval = setTimeout(
              connectWebSocket,
              2000 * reconnectAttempts
            );
          } else {
            setConnectionError("Connection lost. Please refresh the page.");
          }
        };

        newSocket.onerror = (error) => {
          console.error("WebSocket connection error:", error);
          setConnectionError(
            "Failed to connect to real-time transcript service"
          );
          setIsConnected(false);
        };

        // Message handlers
        newSocket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log("Received WebSocket message:", data);

            switch (data.type) {
              case "connection_established":
                console.log("Connection established:", data.server_info);
                break;

              case "full_transcript":
                if (data.meeting_id === meetingId) {
                  setTranscriptSegments(data.transcript_segments || []);
                }
                break;

              case "transcript_update":
                if (data.meeting_id === meetingId) {
                  const segment = data.segment;
                  setTranscriptSegments((prev) => {
                    // Check if segment already exists
                    const existingIndex = prev.findIndex(
                      (s) => s.id === segment.id
                    );
                    if (existingIndex >= 0) {
                      // Update existing segment
                      const updated = [...prev];
                      updated[existingIndex] = segment;
                      return updated;
                    } else {
                      // Add new segment and sort by timestamp
                      return [...prev, segment].sort(
                        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
                      );
                    }
                  });

                  // Callback for parent component
                  if (onTranscriptUpdate) {
                    onTranscriptUpdate(segment);
                  }

                  // Auto-scroll to latest segment
                  setTimeout(() => {
                    if (transcriptContainerRef.current) {
                      transcriptContainerRef.current.scrollTop =
                        transcriptContainerRef.current.scrollHeight;
                    }
                  }, 100);
                }
                break;

              case "meeting_started":
                console.log("Meeting started:", data);
                break;

              case "meeting_ended":
                console.log("Meeting ended:", data);
                if (data.meeting_id === meetingId) {
                  setConnectionError("Meeting has ended.");
                  if (data.meeting_ended === true) {
                    console.log(
                      "Setting meetingEnded to true - Summary tab should be enabled"
                    );
                    setMeetingEnded(true);
                  }
                }
                break;

              case "speaker_update":
                console.log("Speaker update:", data);
                break;

              case "error":
                console.error("Server error:", data.message);
                setConnectionError(data.message || "Unknown error occurred");
                break;

              case "pong":
                // Heartbeat response
                break;

              case "end_meeting":
                if (data.meeting_id === meetingId) {
                  console.log("End meeting message received:", data);
                  if (data.meeting_ended === true) {
                    console.log(
                      "Setting meetingEnded to true from end_meeting - Summary tab should be enabled"
                    );
                    setMeetingEnded(true);
                  }

                  // Add visual indicator that meeting has ended
                  setTranscriptSegments((prev) => [
                    ...prev,
                    {
                      id: "meeting-end",
                      text: "--- Meeting Ended ---",
                      speaker: "System",
                      timestamp: data.timestamp,
                      isSystemMessage: true,
                    },
                  ]);

                  // Optionally call parent callback
                  if (onTranscriptUpdate) {
                    onTranscriptUpdate({
                      type: "meeting_ended",
                      meeting_id: data.meeting_id,
                      summary: data.summary,
                    });
                  }
                }
                break;

              default:
                console.log("Unknown message type:", data.type);
            }
          } catch (error) {
            console.error("Error parsing WebSocket message:", error);
          }
        };
      } catch (error) {
        console.error("Error creating WebSocket connection:", error);
        setConnectionError("Failed to create WebSocket connection");
      }
    };

    // Initial connection
    connectWebSocket();

    // Heartbeat to keep connection alive
    const heartbeatInterval = setInterval(() => {
      if (
        socketRef.current &&
        socketRef.current.readyState === WebSocket.OPEN
      ) {
        socketRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000);

    // Cleanup on unmount
    return () => {
      clearInterval(heartbeatInterval);
      clearTimeout(reconnectInterval);
      if (socketRef.current) {
        if (socketRef.current.readyState === WebSocket.OPEN) {
          socketRef.current.send(
            JSON.stringify({
              type: "end_meeting",
              meeting_id: meetingId,
            })
          );
        }
        socketRef.current.close();
      }
    };
  }, [meetingId, websocketUrl]);

  // Audio player initialization
  useEffect(() => {
    if (!audioUrl || !audioRef.current) return;

    const audio = audioRef.current;
    setIsLoading(true);

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);

      // Highlight current segment based on audio time
      const currentSegment = transcriptSegments.find((segment) => {
        const startTime = segment.start_time || segment.audio_start || 0;
        const endTime = segment.end_time || segment.audio_end || startTime + 3; // Default 3 second duration
        return audio.currentTime >= startTime && audio.currentTime <= endTime;
      });

      if (currentSegment) {
        setHighlightedSegmentId(currentSegment.id);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    const handleError = (e) => {
      console.error("Audio loading error:", e);
      setIsLoading(false);
      setConnectionError("Failed to load audio file");
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
    };
  }, [audioUrl, transcriptSegments]);

  // Audio control functions
  const togglePlayPause = useCallback(() => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handleSeek = useCallback((newTime) => {
    if (!audioRef.current) return;

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, []);

  const handleSegmentClick = useCallback(
    (segment) => {
      const startTime = segment.start_time || segment.audio_start;
      if (!audioRef.current || startTime === undefined) return;

      handleSeek(startTime);
      setHighlightedSegmentId(segment.id);

      // Start playing if not already playing
      if (!isPlaying) {
        audioRef.current.play();
        setIsPlaying(true);
      }
    },
    [handleSeek, isPlaying]
  );

  const handleVolumeChange = useCallback((newVolume) => {
    if (!audioRef.current) return;

    audioRef.current.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  }, []);

  const toggleMute = useCallback(() => {
    if (!audioRef.current) return;

    if (isMuted) {
      audioRef.current.volume = volume;
      setIsMuted(false);
    } else {
      audioRef.current.volume = 0;
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  const skipTime = useCallback(
    (seconds) => {
      if (!audioRef.current) return;

      const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
      handleSeek(newTime);
    },
    [currentTime, duration, handleSeek]
  );

  const formatTime = useCallback((time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, []);

  const getSpeakerColor = useCallback((speaker) => {
    // Generate consistent colors for speakers
    const colors = ["primary", "secondary", "success", "warning", "info"];
    const index = speaker.length % colors.length;
    return colors[index];
  }, []);

  // Function to load existing summary from database
  const loadExistingSummary = async () => {
    try {
      setIsLoadingSummary(true);
      setSummaryError(null);
      const backendUrl = serverConfig.serverURI || "https://laptop:3000";
      console.log(
        "Loading summary from:",
        `${backendUrl}/api/meetings/summary/${meetingId}`
      );
      const response = await fetch(
        `${backendUrl}/api/meetings/summary/${meetingId}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.summary) {
          console.log("Loaded existing summary:", data.summary);
          setSummary(data.summary);
          setSummaryGenerated(true); // Mark that summary exists
          setMeetingEnded(true); // If summary exists, meeting is permanently ended
          return true;
        }
      } else if (response.status !== 404) {
        console.warn("Failed to load existing summary:", response.status);
      }
      return false;
    } catch (error) {
      console.error("Error loading existing summary:", error);
      setSummaryError("Failed to load summary");
      return false;
    } finally {
      setIsLoadingSummary(false);
    }
  };

  // Function to generate summary using ChatGPT
  const generateSummary = async (forceRegenerate = false) => {
    try {
      setIsLoadingSummary(true);
      setSummaryError(null);
      const backendUrl = serverConfig.serverURI || "https://laptop:3000";
      console.log(
        "Generating summary via:",
        `${backendUrl}/api/meetings/process-transcript`
      );

      const response = await fetch(
        `${backendUrl}/api/meetings/process-transcript`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            meeting_id: meetingId,
            transcript_segments: transcriptSegments.filter(
              (segment) => !segment.isSystemMessage
            ),
            force_regenerate: forceRegenerate,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.analysis) {
          console.log("Generated new summary:", data.analysis);
          setSummary(data.analysis);
          setSummaryGenerated(true); // Mark that summary has been generated
          setMeetingEnded(true); // Once summary is generated, meeting is permanently ended
          return true;
        } else {
          setSummaryError(data.message || "Failed to generate summary");
          return false;
        }
      } else {
        const errorText = await response.text();
        setSummaryError(
          `Failed to generate summary: ${response.status} ${errorText}`
        );
        return false;
      }
    } catch (error) {
      console.error("Error generating summary:", error);
      setSummaryError("Failed to generate summary");
      return false;
    } finally {
      setIsLoadingSummary(false);
    }
  };

  // Function to handle tab change and summary loading
  const handleTabChange = async (event, newValue) => {
    console.log(
      `Tab changed to ${newValue}, summaryGenerated: ${summaryGenerated}, summary exists: ${!!summary}`
    );
    setActiveTab(newValue);

    // If switching to Summary tab (tab 1)
    if (newValue === 1) {
      console.log("Switching to Summary tab...");

      // Always try to load existing summary first
      const existingSummaryLoaded = await loadExistingSummary();
      console.log("Existing summary loaded:", existingSummaryLoaded);

      // If no existing summary found, generate new one (first time)
      if (!existingSummaryLoaded && !summaryGenerated) {
        console.log(
          "No existing summary found and never generated, generating new summary..."
        );
        await generateSummary(false); // Generate for first time
      } else if (existingSummaryLoaded) {
        console.log("Existing summary loaded from database");
      } else if (summaryGenerated) {
        console.log("Summary was already generated and loaded");
      }
    }
  };

  return (
    <Box>
      {/* Connection Status */}
      <Box mb={2}>
        <Alert
          severity={
            isConnected ? "success" : connectionError ? "error" : "info"
          }
          icon={
            !isConnected && !connectionError ? (
              <CircularProgress size={20} />
            ) : undefined
          }
        >
          {isConnected
            ? "Connected to real-time transcript service"
            : connectionError
            ? connectionError
            : "Connecting to real-time transcript service..."}
        </Alert>
      </Box>

      {/* Debug: Test Meeting End (only show in development) */}
      {process.env.NODE_ENV === "development" && (
        <Box mb={2}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setMeetingEnded(true)}
            disabled={meetingEnded}
          >
            {meetingEnded ? "Meeting Ended ✓" : "Test: Mark Meeting as Ended"}
          </Button>
          {meetingEnded && (
            <Chip
              label="Meeting ended - Summary tab should be enabled"
              color="success"
              size="small"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
      )}

      {/* Audio Player */}
      {audioUrl && (
        <>
          <audio ref={audioRef} src={audioUrl} preload="metadata" />
          <AudioPlayerContainer>
            <Stack spacing={2}>
              {/* Main Controls */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <Tooltip title="Previous 10s">
                  <IconButton
                    onClick={() => skipTime(-10)}
                    disabled={!audioUrl}
                  >
                    <Replay10 />
                  </IconButton>
                </Tooltip>

                <Tooltip title={isPlaying ? "Pause" : "Play"}>
                  <IconButton
                    onClick={togglePlayPause}
                    disabled={isLoading || !audioUrl}
                    size="large"
                  >
                    {isLoading ? (
                      <CircularProgress size={24} />
                    ) : isPlaying ? (
                      <Pause />
                    ) : (
                      <PlayArrow />
                    )}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Next 10s">
                  <IconButton onClick={() => skipTime(10)} disabled={!audioUrl}>
                    <Forward10 />
                  </IconButton>
                </Tooltip>

                <Typography variant="body2" sx={{ minWidth: "80px" }}>
                  {formatTime(currentTime)} / {formatTime(duration)}
                </Typography>

                <Box sx={{ flexGrow: 1, mx: 2 }}>
                  <Slider
                    value={currentTime}
                    max={duration}
                    onChange={(_, value) => handleSeek(value)}
                    disabled={!audioUrl}
                    size="small"
                  />
                </Box>

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ minWidth: "120px" }}
                >
                  <IconButton onClick={toggleMute} size="small">
                    {isMuted ? <VolumeOff /> : <VolumeUp />}
                  </IconButton>
                  <Slider
                    value={isMuted ? 0 : volume}
                    onChange={(_, value) => handleVolumeChange(value)}
                    min={0}
                    max={1}
                    step={0.1}
                    size="small"
                    sx={{ width: "60px" }}
                  />
                </Stack>
              </Stack>
            </Stack>
          </AudioPlayerContainer>
        </>
      )}

      {/* Tabs for Transcript and Summary */}
      <Box sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="transcript and summary tabs"
        >
          <Tab
            icon={<Assessment />}
            label="Live Transcript"
            iconPosition="start"
            sx={{ textTransform: "none" }}
          />
          <Tab
            icon={<Summarize />}
            label="Summary"
            iconPosition="start"
            sx={{ textTransform: "none" }}
            disabled={false}
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        /* Transcript Tab */
        <TranscriptContainer ref={transcriptContainerRef}>
          <Typography variant="h6" gutterBottom>
            Live Transcript
            {transcriptSegments.length > 0 && (
              <Chip
                label={`${transcriptSegments.length} segments`}
                size="small"
                sx={{ ml: 1 }}
              />
            )}
            {meetingEnded && (
              <Chip
                label="Meeting Ended"
                size="small"
                color="warning"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {transcriptSegments.length === 0 ? (
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign="center"
            >
              {isLoading
                ? "Loading existing transcript..."
                : isConnected
                ? "Waiting for transcript segments..."
                : "No transcript available"}
            </Typography>
          ) : (
            transcriptSegments.map((segment, index) => (
              <TranscriptSegment
                key={segment.id || index}
                isHighlighted={segment.id === highlightedSegmentId}
                isClickable={Boolean(audioUrl) && !segment.isSystemMessage}
                onClick={() =>
                  audioUrl &&
                  !segment.isSystemMessage &&
                  handleSegmentClick(segment)
                }
                sx={{
                  ...(segment.isSystemMessage && {
                    backgroundColor: "action.hover",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    textAlign: "center",
                  }),
                }}
              >
                {segment.isSystemMessage ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic", py: 1 }}
                  >
                    {segment.text}
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Chip
                      label={segment.speaker || "Unknown"}
                      size="small"
                      color={getSpeakerColor(segment.speaker || "Unknown")}
                      variant="outlined"
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body1">{segment.text}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(segment.timestamp).toLocaleTimeString()}
                        {(segment.start_time !== undefined ||
                          segment.audio_start !== undefined) && (
                          <>
                            {" "}
                            •{" "}
                            {formatTime(
                              segment.start_time || segment.audio_start
                            )}
                          </>
                        )}
                        {segment.confidence && segment.confidence < 1 && (
                          <>
                            {" "}
                            • {Math.round(segment.confidence * 100)}% confidence
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </TranscriptSegment>
            ))
          )}
        </TranscriptContainer>
      )}

      {activeTab === 1 && (
        /* Summary Tab */
        <Box>
          {!summaryGenerated && !summary && !isLoadingSummary && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Click the Summary tab to generate a meeting summary. Once
              generated, the meeting will be marked as permanently ended.
            </Alert>
          )}

          {isLoadingSummary ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                {summaryGenerated
                  ? "Regenerating summary..."
                  : "Generating summary..."}
              </Typography>
            </Box>
          ) : summaryError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {summaryError}
              <Button
                onClick={() => generateSummary(true)}
                variant="outlined"
                size="small"
                sx={{ ml: 2 }}
              >
                Retry
              </Button>
            </Alert>
          ) : summary ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Meeting Summary
                {summaryGenerated && (
                  <Button
                    onClick={() => generateSummary(true)}
                    variant="outlined"
                    size="small"
                    sx={{ ml: 2 }}
                    disabled={isLoadingSummary}
                  >
                    Regenerate
                  </Button>
                )}
              </Typography>

              <Stack spacing={3}>
                {summary.summary && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Overview
                      </Typography>
                      <Typography variant="body1">{summary.summary}</Typography>
                    </CardContent>
                  </Card>
                )}

                {summary.key_points && summary.key_points.length > 0 && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Key Points
                      </Typography>
                      <Stack spacing={1}>
                        {summary.key_points.map((point, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            sx={{ display: "flex", alignItems: "flex-start" }}
                          >
                            <Box
                              component="span"
                              sx={{ mr: 1, mt: 0.5, fontSize: "0.8em" }}
                            >
                              •
                            </Box>
                            {point}
                          </Typography>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {summary.action_items && summary.action_items.length > 0 && (
                  <Card>
                    <CardContent>
                      <Typography
                        variant="h6"
                        gutterBottom
                        color="warning.main"
                      >
                        Action Items
                      </Typography>
                      <Stack spacing={1}>
                        {summary.action_items.map((item, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            sx={{ display: "flex", alignItems: "flex-start" }}
                          >
                            <Box
                              component="span"
                              sx={{ mr: 1, mt: 0.5, fontSize: "0.8em" }}
                            >
                              ✓
                            </Box>
                            {item}
                          </Typography>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {summary.insights && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="info.main">
                        Insights
                      </Typography>
                      <Stack spacing={2}>
                        {summary.insights.achievements &&
                          summary.insights.achievements.length > 0 && (
                            <Box>
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                color="success.main"
                                sx={{ fontWeight: "bold" }}
                              >
                                Achievements
                              </Typography>
                              <Stack spacing={1}>
                                {summary.insights.achievements.map(
                                  (achievement, index) => (
                                    <Typography
                                      key={index}
                                      variant="body2"
                                      sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                      }}
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          mr: 1,
                                          mt: 0.5,
                                          fontSize: "0.8em",
                                        }}
                                      >
                                        ✓
                                      </Box>
                                      {achievement}
                                    </Typography>
                                  )
                                )}
                              </Stack>
                            </Box>
                          )}

                        {summary.insights.challenges &&
                          summary.insights.challenges.length > 0 && (
                            <Box>
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                color="error.main"
                                sx={{ fontWeight: "bold" }}
                              >
                                Challenges
                              </Typography>
                              <Stack spacing={1}>
                                {summary.insights.challenges.map(
                                  (challenge, index) => (
                                    <Typography
                                      key={index}
                                      variant="body2"
                                      sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                      }}
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          mr: 1,
                                          mt: 0.5,
                                          fontSize: "0.8em",
                                        }}
                                      >
                                        ⚠️
                                      </Box>
                                      {challenge}
                                    </Typography>
                                  )
                                )}
                              </Stack>
                            </Box>
                          )}

                        {summary.insights.deliverables &&
                          summary.insights.deliverables.length > 0 && (
                            <Box>
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                color="primary.main"
                                sx={{ fontWeight: "bold" }}
                              >
                                Deliverables
                              </Typography>
                              <Stack spacing={1}>
                                {summary.insights.deliverables.map(
                                  (deliverable, index) => (
                                    <Typography
                                      key={index}
                                      variant="body2"
                                      sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                      }}
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          mr: 1,
                                          mt: 0.5,
                                          fontSize: "0.8em",
                                        }}
                                      >
                                        📦
                                      </Box>
                                      {deliverable}
                                    </Typography>
                                  )
                                )}
                              </Stack>
                            </Box>
                          )}

                        {summary.insights.open_questions &&
                          summary.insights.open_questions.length > 0 && (
                            <Box>
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                color="warning.main"
                                sx={{ fontWeight: "bold" }}
                              >
                                Open Questions
                              </Typography>
                              <Stack spacing={1}>
                                {summary.insights.open_questions.map(
                                  (question, index) => (
                                    <Typography
                                      key={index}
                                      variant="body2"
                                      sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                      }}
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          mr: 1,
                                          mt: 0.5,
                                          fontSize: "0.8em",
                                        }}
                                      >
                                        ❓
                                      </Box>
                                      {question}
                                    </Typography>
                                  )
                                )}
                              </Stack>
                            </Box>
                          )}

                        {/* Show legacy insights format if structured insights not available */}
                        {Array.isArray(summary.insights) &&
                          summary.insights.length > 0 && (
                            <Box>
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                color="info.main"
                                sx={{ fontWeight: "bold" }}
                              >
                                General Insights
                              </Typography>
                              <Stack spacing={1}>
                                {summary.insights.map((insight, index) => (
                                  <Typography
                                    key={index}
                                    variant="body2"
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{ mr: 1, mt: 0.5, fontSize: "0.8em" }}
                                    >
                                      💡
                                    </Box>
                                    {insight}
                                  </Typography>
                                ))}
                              </Stack>
                            </Box>
                          )}

                        {/* Show message if no insights */}
                        {(!summary.insights.achievements ||
                          summary.insights.achievements.length === 0) &&
                          (!summary.insights.challenges ||
                            summary.insights.challenges.length === 0) &&
                          (!summary.insights.deliverables ||
                            summary.insights.deliverables.length === 0) &&
                          (!summary.insights.open_questions ||
                            summary.insights.open_questions.length === 0) &&
                          (!Array.isArray(summary.insights) ||
                            summary.insights.length === 0) && (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ fontStyle: "italic" }}
                            >
                              No specific insights found in this meeting.
                            </Typography>
                          )}
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {summary.keywords && summary.keywords.length > 0 && (
                  <Card>
                    <CardContent>
                      <Typography
                        variant="h6"
                        gutterBottom
                        color="secondary.main"
                      >
                        Keywords
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {summary.keywords.map((keyword, index) => (
                          <Chip
                            key={index}
                            label={keyword}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign="center"
              sx={{ py: 4 }}
            >
              {!summaryGenerated
                ? "Summary will be generated automatically when you click this tab."
                : 'Summary was generated and saved. Use "Regenerate" button to create a new one.'}
              {!summaryGenerated && transcriptSegments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    onClick={() => generateSummary(false)}
                    variant="contained"
                    startIcon={<Summarize />}
                  >
                    Generate Summary
                  </Button>
                </Box>
              )}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default RealTimeTranscript;
